import type { Task } from './Task';
import type { ServiceArea } from './ForemanArea';

export interface Activity {
  id: number;
  activityName: string;
  activityNumber: string;
  unitOfMeasure: string;
  scheduled2Rate?: number | null;
  serviceAreaId?: number | null;
  contractYear?: number | null;
  point?: boolean;
  linear?: boolean;
  activityProfile?: string;
  /**
   * The second free-text profile column.
   *
   * Which of the two carries the name varies by database: the training database has it here
   * with `activityProfile` blank on every row. Display `activityProfile || profile`, but keep
   * the editable field bound to `activityProfile` - writing the name back into the other column
   * would quietly duplicate it.
   */
  profile?: string | null;
  activityType?: string;
  locationCode?: string;
  classification?: string;
  threshold?: number | null;
  serviceArea?: ServiceArea | null;
  tasks?: Task[];
  activityCostings?: Array<{
    id: number;
    serviceAreaId: number;
    contractYear: number | null;
    scheduled2Rate: number | null;
    serviceArea?: ServiceArea | null;
  }>;

  /**
   * Whether work instructions exist for this activity. Always present; derived server-side
   * from a timestamp, so a list can show the link without carrying the prose.
   */
  hasWorkInstructions?: boolean;
  /**
   * The prose itself. Present on GET /api/activities/:id, and on a list only when it was
   * requested with ?include=workInstructions. Null means it was not fetched, which is not the
   * same as the activity having none - read hasWorkInstructions for that.
   */
  workInstructions?: {
    purpose: string | null;
    steps: string[];
    /** The unparsed cell, for rendering a row the step parser mangled. */
    stepsRaw: string | null;
    updatedAt: string | null;
  } | null;

  /**
   * Activity profile attributes, loaded from the Activity Profiles export and served flat.
   * `activityProfile` above is the profile NAME and remains the editable field.
   *
   * Every boolean defaults to false, so profileLoadedAt is what distinguishes "no profile
   * matched this name" from "a profile that says No to everything".
   */
  displayQuantityToUser?: boolean;
  requiresLocationalData?: boolean;
  askForRoad?: boolean;
  mechanicEquipmentActivity?: boolean;
  signActivity?: boolean;
  culvertActivity?: boolean;
  cattleGuardActivity?: boolean;
  wildlifeActivity?: boolean;
  rockfallActivity?: boolean;
  /** Null = do not ask; OPTIONAL = offer the picker; MANDATORY = block submit without one. */
  restAreaActivity?: 'OPTIONAL' | 'MANDATORY' | null;
  structureActivity?: 'OPTIONAL' | 'MANDATORY' | null;
  requiresStructure?: boolean;
  briefAndEmergingActivity?: boolean;
  blockActivity?: boolean;
  blockType?: string | null;
  /** The checkbox's initial state. Null = do not show the question at all. */
  displayEntireRoadQuestion?: 'CHECKED' | 'UNCHECKED' | null;
  numberOfPasses?: number | null;
  /** Null = the road filter does not apply; false = restrict to non-numbered roads. */
  numberedRoadActivity?: boolean | null;
  isLunch?: boolean;
  profileLoadedAt?: string | null;
  /**
   * When a profile flag was last changed by hand here, cleared whenever the import writes the
   * row. Set and newer than profileLoadedAt means the next import will replace those edits.
   */
  profileEditedAt?: string | null;
} 

export enum ActivityType {
  Routine = 'Routine',
  Overhead = 'Overhead',
  Quantified = 'Quantified',
}